import { HELP_ANCHORS, SCOPE_LEVEL_WORD } from "@keydials/ui";
import { FIRST_RUN_TOUR_ID } from "./tourState";
import type { HelpGuide } from "./types";

/**
 * The "Helpmeet" how-to catalog: interactive walkthroughs the user can search for and run. Each guide
 * spotlights a real control and shows a coach-mark beside it, stepping through the task. Steps target
 * controls by their `data-help-anchor` id (HELP_ANCHORS) — the same ids the components render — so the
 * guides can never point at a control that no longer exists.
 *
 * They can, though, point at one that has MOVED, which is the failure this file is written against: the
 * anchor still resolves, the tour still runs, and it teaches the wrong route. That is what happened
 * between #193 and here — `manage-members` spent twenty PRs telling people to click a Members button in
 * the dial bar that had moved onto the Settings tab. So: when a control changes home, the guide's prose
 * is as much part of the change as the anchor, and `catalog.test.ts` pins the routes that have already
 * drifted once.
 *
 * Most guides run inside a dial (context "dial"); when a target lives behind a tab, menu or slide-over the
 * previous step opens it (advance: "target-click") and the next step waits for it to appear (waitMs). A
 * `hint` tells the user how to reach a control the tour can't find (e.g. they lack the permission for it).
 *
 * KEY-813: when a step's control lives on a DIFFERENT screen from the step before — up to the section, into an
 * action point, onto Your settings — and that step isn't a click that takes you there, the step carries a
 * `reach`: the thing to do first, said as an instruction. The overlay shows it as "First: …" whenever the
 * control isn't on screen, and `catalog.test.ts` checks every such step has one against the screen map in
 * `anchorViews.ts`. A click step's Next is a quieter Skip, and says which highlighted control to click.
 *
 * A guide with `dialKind` is only offered on that kind of dial. Setting a status is the case that forced
 * this: on a simple dial the action point has its own status pill, while an itemised one is scored per
 * item behind the Items tab, so one guide cannot honestly describe both. Everything else here — files,
 * people, messages, the logo, subscribing — works the same either way and stays untagged.
 *
 * A STEP can carry `dialKind` and `requires` too (KEY-535), and the first-run tour below is why: six of its
 * seven steps are the same on both kinds of dial, so splitting it into two guides would have doubled the
 * prose that can drift and put two near-identical entries in the launcher. `resolveGuide` cuts the steps down
 * once, at start, so the overlay still counts "step n of" against a list that matches what you'll be shown.
 */
export const GUIDES: HelpGuide[] = [
  {
    // KEY-535. The only guide that ever starts on its own — offered once per person, the first time a dial
    // finishes loading in front of them, and never again whether they finish it or press ✕.
    //
    // Seven steps, deliberately. It answers the five questions somebody who has never seen a dial actually
    // has (how do I get about, how do I keep up, how do I record what I found, where's help, how do I tell
    // you something's wrong) and stops. Everything else in this catalog — files, review dates, guidance,
    // members, guests, licensing, structure, activity, section statuses — is one search away in the same
    // drawer, and a first-run tour that covered them would be a manual nobody reads to the end of.
    //
    // Nothing here uses `advance: "target-click"`: not making anyone click anything is most of what "keep it
    // light" means. Every step also carries a `hint`, because the tour can be restarted from the Help drawer
    // from anywhere, at which point several of these controls are legitimately off screen.
    id: FIRST_RUN_TOUR_ID,
    title: "Show me around a dial",
    // Keywords chosen to avoid "status", "file", "message" and "subscribe" as bare tokens: the tour sits
    // first in this list, so sharing a token with a specific guide would let it outrank the actual answer to
    // "how do I change a status". `search.test.ts` pins that.
    summary: "A quick look round a dial — getting about, keeping up, and where to turn when you're stuck.",
    keywords: [
      "tour", "walkthrough", "walk", "round", "around", "show", "getting", "started", "start",
      "introduction", "intro", "guided", "first", "time", "new", "restart", "again", "basics", "lost",
    ],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.dialFace,
        title: "This dial is the menu",
        // The opening line doubles as the offer: the tour arrives unasked, so the way out is the first thing
        // it says rather than something to hunt for in a corner. KEY-813: "try it once the tour's done" — a
        // click on the dial now would move the panel away from what the next steps point at.
        body: "Two minutes? Press Next — or ✕ to skip. The dial itself is how you get about: click a band for a section, or a wedge for one action point, and it opens beside you — try it once the tour's done.",
        waitMs: 3000,
        hint: "Open one of your dials to follow along — the tour is about the big dial on the left.",
      },
      {
        anchor: HELP_ANCHORS.scopeLevel,
        // Built from the same constant the chip renders, so renaming a level changes both at once.
        title: "It says where you are",
        body: `This chip names the level you have open — ${SCOPE_LEVEL_WORD.dial}, ${SCOPE_LEVEL_WORD.section} or ${SCOPE_LEVEL_WORD.action} — and the link beside it takes you back up one.`,
        waitMs: 3000,
        hint: "The chip sits at the top of the panel beside the dial.",
      },
      {
        anchor: HELP_ANCHORS.dialSubscribe,
        title: "Keep up with the whole dial",
        body: "Subscribe follows this dial, so you hear when anything on it moves; click it again to stop. The caret beside it picks how you're told.",
        requires: ["subscribe"],
        waitMs: 3000,
        hint: "Subscribe is in the bar above the dial.",
      },
      {
        anchor: HELP_ANCHORS.sectionRowSubscribe,
        // The real rule from lib/subscriptions.ts, not a simplification of it: people assume these compete.
        title: "Or only the part that's yours",
        body: "On the whole dial's Sections list, each section has its own bell. Subscribe to something broad and it covers everything under it — then mute one section or action point as an exception.",
        requires: ["subscribe"],
        waitMs: 6000,
        reach: "Go up to the whole dial — use the link beside the level chip until the chip says Whole dial. Then open its Sections tab.",
        hint: "The bells sit on the right of each section in the whole dial's Sections list.",
      },
      {
        anchor: HELP_ANCHORS.apStatus,
        title: "Record where something's got to",
        body: "Click any wedge on the dial to open an action point, then click its status pill to say where it stands. Anything you're not allowed to set is greyed out, and some ask you to fill a field in first.",
        dialKind: "simple",
        requires: ["setStatus"],
        waitMs: 8000,
        reach: "Click a wedge on the dial to open an action point.",
        hint: "The status pill is near the top of the action point's panel.",
      },
      {
        anchor: HELP_ANCHORS.apTabItems,
        // Says "worked out from its items" rather than promising a pill per check: on an itemised dial an
        // action point with no items yet still carries its own pill (VSegment.itemActive), and a brand-new
        // dial — exactly the first-run case — can be full of them.
        title: "Record where something's got to",
        body: "Click a wedge on the dial to open an action point. This dial is scored item by item — an action point's colour is worked out from its items — so you set a status on its Items tab.",
        dialKind: "itemised",
        requires: ["setStatus"],
        waitMs: 8000,
        reach: "Click a wedge on the dial to open an action point.",
        hint: "The Items tab is among the action point's tabs, under its name.",
      },
      {
        anchor: HELP_ANCHORS.helpButton,
        // Self-referential on purpose: this step IS the "let me do that again later" the issue asked for.
        title: "Everything else lives here",
        body: "Help answers “how do I…?” with a short walkthrough like this one, for anything the tour skipped — and you can start this tour again from the bottom of it.",
        waitMs: 3000,
        hint: "Help is at the top right of the page, just left of Feedback — the mortarboard icon in a coloured tile. On a narrow screen it is the icon on its own, without its label.",
      },
      {
        anchor: HELP_ANCHORS.feedbackButton,
        title: "And tell us when it's wrong",
        body: "Feedback sends a bug or an idea straight to the team. If they need to know more they'll ask you back here, and a reply shows up beside it.",
        requires: ["feedback"],
        waitMs: 3000,
        hint: "Feedback is at the top of the page, beside Help.",
      },
    ],
  },
  {
    id: "change-status",
    title: "Change a status",
    summary: "Set or update the status of an action point.",
    keywords: ["status", "change", "set", "update", "rag", "progress", "mark", "state", "colour", "color"],
    context: "dial",
    // Simple dials only: the action point carries its own status pill. On an itemised dial that pill
    // isn't there at all — see "change-item-status".
    dialKind: "simple",
    steps: [
      {
        anchor: HELP_ANCHORS.apStatus,
        title: "Change the status",
        body: "Open an action point (click a wedge on the dial), then click its status pill and pick the one you want. Any you can't set are greyed out, and some ask you to fill in a field or set a review date first.",
        reach: "Click a wedge on the dial to open an action point.",
        hint: "The status pill is near the top of the action point's panel.",
      },
    ],
  },
  {
    id: "change-item-status",
    title: "Change an item's status",
    summary: "Set the status of an item on an itemised dial.",
    keywords: ["status", "change", "set", "update", "item", "items", "rag", "progress", "mark", "state", "check", "score"],
    context: "dial",
    dialKind: "itemised",
    steps: [
      {
        anchor: HELP_ANCHORS.apTabItems,
        title: "Open the Items tab",
        body: "Open an action point, then click its Items tab. This dial is scored item by item rather than as a whole, so an action point's colour comes from its items.",
        advance: "target-click",
        target: "Items tab",
        reach: "Click a wedge on the dial to open an action point.",
        hint: "The Items tab is among the action point's tabs, under its name.",
      },
      {
        // KEY-813: the item cards are collapsed, so their pills aren't on the page until one is opened — a step
        // of its own rather than an aside in the next one.
        anchor: HELP_ANCHORS.itemRowToggle,
        title: "Open an item",
        body: "Click an item's name to open its card.",
        advance: "target-click",
        target: "item",
        waitMs: 4000,
        // KEY-460: the Items tab can be reachable but locked, which is the likeliest reason someone runs this
        // guide and finds nothing to click. Say so here rather than leaving them at a dead end.
        hint: "No items yet? Add one with ‘Add item’. If the tab says the action point needs fields filled in first, answer those on the Key Info tab.",
      },
      {
        anchor: HELP_ANCHORS.apStatus,
        title: "Set the status on each check",
        // Item cards reopen on the tab last chosen (KEY-774), so "it opens on Progress" is no longer a promise.
        body: "If the card isn't on its Progress tab, click Progress. Each check has a status pill — click one and pick a status. The action point's own colour is worked out from all of them.",
        // Every item's pills share this anchor, so the spotlight lands on whichever the browser finds first —
        // the body says "each check" rather than pointing at one.
        waitMs: 4000,
        hint: "Open an item (click its name) to reveal its status pills — there's one per check.",
      },
    ],
  },
  {
    id: "add-files",
    title: "Add a file to an action point",
    // "evidence" stays in the keywords: the word is gone from what people read, but anyone who still
    // thinks in it — or read it in older guidance — should land here rather than on nothing.
    summary: "Upload a file — a photo, a certificate, a form — to an action point.",
    keywords: ["add", "file", "files", "upload", "evidence", "attach", "document", "proof", "attachment", "photo"],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.apTabFiles,
        title: "Open the Files tab",
        body: "Open an action point, then click its Files tab — that's where its files live. Sections and individual items have their own Files tabs too, so a file lands wherever you upload it.",
        advance: "target-click",
        target: "Files tab",
        reach: "Click a wedge on the dial to open an action point.",
        hint: "The Files tab is among the action point's tabs, under its name.",
      },
      {
        anchor: HELP_ANCHORS.apUploadEvidence,
        title: "Upload your file",
        // The button's wording uses the dial's own word for an action point, so it isn't quoted here.
        body: "Click the dashed Upload button and pick a file. You can tag it with one of this dial's own tags, or type a new one, and it's attached here; it can move the status on for you.",
        waitMs: 4000,
        hint: "You'll need edit access to this dial to add files.",
      },
      {
        anchor: HELP_ANCHORS.sectionTabFiles,
        title: "Files that belong to the whole section",
        body: "Now open the section's Files tab. It gathers everything attached anywhere beneath it — including files given as the answer to a field. Upload there for anything that isn't about one action point.",
        waitMs: 6000,
        reach: "Go up one level to the section — use the link beside the level chip at the top of the panel.",
        hint: "The section's tabs sit under its name: Overview, Guidance, Files and so on.",
      },
    ],
  },
  {
    id: "fill-in-fields",
    title: "Answer an action point's questions",
    summary: "Fill in what an action point asks you — and unlock its items.",
    keywords: ["field", "fields", "info", "attribute", "question", "answer", "required", "locked", "blocked", "detail", "form", "overview", "to do", "todo", "checklist", "missing"],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.apOverviewChecklist,
        title: "See what's still to answer",
        // KEY-813: no "pick one" — clicking a row jumps to Key Info, which makes the next step's click pointless.
        body: "An action point's Overview tab lists the questions it asks and shows which still need an answer.",
        waitMs: 3000,
        reach: "Open an action point (click a wedge on the dial) and click its Overview tab.",
        // The list only renders when the action point has fields you can see, and says something shorter
        // when its status means nothing is owed — so its absence is an answer, not a failure.
        hint: "If you're on the Overview tab and there's no list, this action point asks nothing of you, or it's marked as not needed.",
      },
      {
        anchor: HELP_ANCHORS.apTabInfo,
        title: "Open the Key Info tab",
        body: "An action point's own questions — who owns it, when it was last checked, whatever this dial asks — live on the Key Info tab. The section's details show there too, to read; change them on the section itself.",
        advance: "target-click",
        target: "Key Info tab",
        // The tab only renders when the action point has fields you're allowed to see, so its absence is
        // a real answer, not a failure: say that rather than leaving someone hunting for it.
        hint: "No Key Info tab means this action point asks nothing of you — either it declares no fields, or you can't see the ones it has.",
      },
      {
        anchor: HELP_ANCHORS.apTabItems,
        title: "Why it can matter",
        body: "Some action points can't be worked until certain fields are answered — until then the Items tab is flagged and its items are held back. Fill the named fields in on Key Info and the items open up.",
        waitMs: 3000,
        hint: "Only itemised dials have an Items tab to hold back — on a simple dial, fields are just fields.",
      },
    ],
  },
  {
    id: "find-guidance",
    title: "Read the guidance left for you",
    summary: "See what's being asked for, and watch any videos.",
    keywords: ["guidance", "help", "advice", "video", "videos", "watch", "explain", "what", "expected", "criteria", "instructions"],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.apTabGuidance,
        title: "Open an action point's Guidance tab",
        body: "Open an action point and click its Guidance tab. Whoever set this dial up can leave guidance there — what good looks like, what to gather. A ▶ and a count in the tab mean there are videos as well as words.",
        reach: "Click a wedge on the dial to open an action point.",
        hint: "The Guidance tab is among the action point's tabs, under its name.",
      },
      {
        // KEY-542: a section's guidance used to be buried at the top of its Overview tab, where it read as
        // part of the description. It has a tab of its own now, so it is worth saying where.
        anchor: HELP_ANCHORS.sectionTabGuidance,
        title: "A section has its own Guidance too",
        body: "Guidance about a whole area of the dial sits on the section's own Guidance tab, next to Overview — the same words-then-videos-then-files shape as an action point's. Items carry their guidance inline, where you score them.",
        reach: "Go up one level to the section — use the link beside the level chip at the top of the panel.",
        hint: "Or click a band on the dial to open a section, then find the Guidance tab beside Overview.",
      },
      {
        // KEY-534: and the level above both — how to work the dial as a whole.
        anchor: HELP_ANCHORS.dialTabGuidance,
        title: "And the dial as a whole",
        // "After Overview (and About, if it has one)": About only exists when the dial declares dial-level
        // fields, so on most dials Guidance sits next to Overview.
        body: "How to work the dial itself — where to start, what order to go in — sits on the whole dial's Guidance tab, after Overview (and About, if it has one), with any documents and links that go with it. One video at most, and it's part of the published version, so it's the guidance that went with the version your dial is on.",
        reach: "Go up to the whole dial — use the link beside the level chip until the chip says Whole dial.",
        hint: "On the whole dial, Guidance comes after Overview (and About, if the dial has one).",
      },
    ],
  },
  {
    id: "schedule-review",
    title: "Set a date to check it again",
    summary: "Have a status come round again for another look.",
    keywords: ["review", "date", "expiry", "expire", "renew", "recur", "remind", "reminder", "schedule", "revert", "due"],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.apScheduleReview,
        title: "Open the review sheet",
        body: "Open an action point. Beside its status is ‘Schedule review’ (a clock) — click it; on an item, the clock sits beside each check's status. On the date you give it, the status drops back on its own — so a certificate or a check can't quietly stay green for ever.",
        advance: "target-click",
        target: "Schedule review button",
        reach: "Click a wedge on the dial to open an action point.",
        hint: "The clock sits beside a status you can set — on the action point, or beside each check inside an item. Some statuses don't offer one.",
      },
      {
        anchor: HELP_ANCHORS.apReviewDate,
        title: "Choose what happens",
        body: "Pick the date, then what it should drop back to and how far ahead to warn you. You can make it repeat, so a monthly check schedules itself.",
        waitMs: 3000,
        hint: "Click ‘Schedule review’ (the clock beside the status) to open this sheet.",
      },
    ],
  },
  {
    id: "section-status",
    title: "Give a whole section a status",
    summary: "Give a section its own status, shown as a coloured edge.",
    keywords: ["section", "status", "whole", "edge", "ring", "overall", "summary", "set", "colour", "color"],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.sectionStatus,
        title: "Use the section's status pill",
        body: "Open a section and use the status pill beside its name. It colours the section's outer edge on the dial, on top of whatever its action points say.",
        reach: "Click a band on the dial to open a section.",
        hint: "Not every dial is set up to carry a section status — if there's no pill beside the section name, this one isn't. On a group dial, a section opens its subdial instead.",
      },
    ],
  },
  {
    id: "send-message",
    title: "Send a message",
    summary: "Post a message in an action point's discussion.",
    keywords: ["message", "chat", "comment", "discussion", "reply", "post", "talk", "conversation"],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.apTabDiscussion,
        title: "Open the Discussion tab",
        body: "Open an action point, then click its Discussion tab to see the conversation. Individual items have their own discussions too.",
        advance: "target-click",
        target: "Discussion tab",
        reach: "Click a wedge on the dial to open an action point.",
        hint: "The Discussion tab is among the action point's tabs, under its name.",
      },
      {
        anchor: HELP_ANCHORS.messageEditor,
        title: "Write your message",
        body: "Type your message in the editor. You can format it, and reply to someone else's message.",
        waitMs: 4000,
        hint: "You'll need to be a member of this dial to post.",
      },
      {
        anchor: HELP_ANCHORS.messageSend,
        title: "Post it",
        body: "Click Post to send your message (or Reply, if you're responding to someone).",
        waitMs: 2000,
        hint: "Post sits under the editor. If it isn't there, you may not have permission to post on this dial.",
      },
    ],
  },
  {
    // KEY-559: the read-only transcripts. Posting is always on one action point (the guide above); these
    // are where you catch up on everything said beneath a section, or across the dial.
    id: "read-discussion",
    title: "Catch up on everything that's been said",
    summary: "Read every message in a section, or across the whole dial, in one place.",
    keywords: ["discussion", "messages", "transcript", "conversation", "chat", "comments", "catch up", "all", "everything", "section", "dial"],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.sectionTabDiscussion,
        title: "A section's Discussion tab",
        body: "Open a section — its Discussion tab gathers everything said in its action points and on their items, labelled with where each message was said. It's for reading — to reply, open the action point from there.",
        reach: "Click a band on the dial to open a section.",
        hint: "The Discussion tab is among the section's tabs, under its name.",
      },
      {
        anchor: HELP_ANCHORS.dialTabDiscussion,
        title: "Or the whole dial's",
        body: "The whole dial's Discussion tab does the same across every section you can see. On a group dial it also includes what's been said in its subdials.",
        reach: "Go up to the whole dial — use the link beside the level chip until the chip says Whole dial.",
        // KEY-813: Coming up and Look back now sit between Sections and Discussion, so "after Sections" was wrong.
        hint: "On the whole dial, find the Discussion tab further along from Sections (Subdials on a group dial).",
      },
    ],
  },
  {
    id: "subscribe",
    title: "Follow a dial, or stop following it",
    summary: "Get told when things change — or turn that off.",
    keywords: ["subscribe", "unsubscribe", "notifications", "notify", "follow", "alerts", "email", "text", "sms", "mute"],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.dialSubscribe,
        title: "Follow the whole dial",
        body: "Use the Subscribe button in the dial bar to follow this dial; click it again to unsubscribe. The caret beside it chooses which channels notify you.",
        hint: "Open a dial — the Subscribe button is in the bar above it.",
      },
      {
        anchor: HELP_ANCHORS.apSubscribe,
        title: "Or follow just one action point",
        body: "An action point has its own Subscribe button, at the right of the row under its name — it follows just that part, so you can keep up with only what you're responsible for.",
        // Reached by opening an action point rather than by clicking the previous step, so it needs its
        // own patience and its own way out.
        waitMs: 6000,
        reach: "Click a wedge on the dial to open an action point.",
        hint: "Subscribe is at the right-hand end of the row under the action point's name.",
      },
      {
        anchor: HELP_ANCHORS.sectionSubscribe,
        title: "Or a whole section",
        body: "A section's Subscribe sits beside its name and covers every action point in it. Follow something broad, then mute one part of it as an exception.",
        waitMs: 6000,
        reach: "Go up one level to the section — use the link beside the level chip at the top of the panel.",
        hint: "Or click a band on the dial to open a section — Subscribe is beside its name.",
      },
    ],
  },
  {
    id: "see-activity",
    title: "See what changed",
    summary: "Find who changed what, and when.",
    keywords: ["activity", "history", "log", "audit", "changed", "who", "when", "trail", "recent", "track"],
    context: "dial",
    steps: [
      {
        // KEY-813: not a click step. Clicking Activity used to advance straight to a step about ANOTHER screen,
        // so nobody ever saw a card beside the trail they had just opened.
        anchor: HELP_ANCHORS.apTabActivity,
        title: "Open the Activity tab",
        body: "Open an action point and click its Activity tab. Every status change, file, item, member and structure edit is recorded there.",
        reach: "Click a wedge on the dial to open an action point.",
        hint: "The Activity tab is among the action point's tabs, under its name.",
      },
      {
        anchor: HELP_ANCHORS.sectionTabActivity,
        title: "Or zoom out",
        body: "A section and the dial itself have the same tab, each widening what you see. Chat is deliberately left out — messages stay in Discussion.",
        waitMs: 6000,
        reach: "Go up one level to the section — use the link beside the level chip at the top of the panel. Then open its Activity tab.",
        hint: "The whole dial has an Activity tab too.",
      },
    ],
  },
  {
    id: "manage-members",
    title: "Give someone access to a dial",
    summary: "Let someone onto a dial, and choose what they can do.",
    keywords: ["members", "member", "people", "add", "invite", "access", "grant", "share", "role", "permission", "remove", "seat"],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.dialSettings,
        title: "Open Settings (not the Members tab)",
        // The dial's own Members tab is read-only (owner, avatars, last updated), and it is the first
        // place anyone looking for this will try. Say where the controls actually are before they hunt.
        body: "The Members tab shows who's on this dial, but you can't change anything there. Who may open it, and what they may do, is a dial setting — so it lives on the Settings tab.",
        advance: "target-click",
        target: "Settings tab",
        reach: "Go up to the whole dial — use the link beside the level chip until the chip says Whole dial.",
        hint: "Settings is the last tab on the whole dial's tab strip (it may be scrolled off to the right).",
      },
      {
        anchor: HELP_ANCHORS.dialPeople,
        title: "Choose People",
        body: "People is the first entry in the settings menu — Settings usually opens on it already. Everyone who can open this dial, and what each of them can do.",
        advance: "target-click",
        target: "People entry",
        waitMs: 3000,
        hint: "People is the first entry in the Settings menu — beside the page, or above it on a narrow screen.",
      },
      {
        anchor: HELP_ANCHORS.memberGiveAccess,
        title: "Add someone",
        body: "Click ‘Give access to…’. It also tells you how many licence seats are left before you add anyone.",
        advance: "target-click",
        target: "‘Give access to…’ button",
        waitMs: 3000,
        hint: "Only dial admins and managers can add people; everyone else sees the list read-only.",
      },
      {
        anchor: HELP_ANCHORS.memberUserSelect,
        title: "Choose a person",
        body: "Pick the person you want to give access to, or invite someone who isn't in your organisation yet.",
        waitMs: 4000,
        hint: "Click ‘Give access to…’ to open the form.",
      },
      {
        anchor: HELP_ANCHORS.memberRoleSelect,
        title: "Pick their role",
        body: "Choose a role — Reader, Reviewer, Editor or Admin. To limit it to one section or action point, open ‘Advanced — limit to part of this dial’ above.",
      },
      {
        anchor: HELP_ANCHORS.memberAddGrant,
        title: "Give them access",
        body: "Click ‘Give access’ to finish (‘Invite & give access’, if they're new to your organisation).",
      },
    ],
  },
  {
    id: "check-licence-places",
    title: "See how many people your licence covers",
    summary: "Find out how many places you have left before you run out of them.",
    keywords: [
      "licence", "license", "seat", "seats", "place", "places", "users", "how many",
      "limit", "allowance", "full", "plan", "billing", "subscription", "can't add",
    ],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.dialSettings,
        title: "Open Settings",
        body: "Your licence is shown per dial, on the Settings tab — the figure covers your whole account.",
        advance: "target-click",
        target: "Settings tab",
        reach: "Go up to the whole dial — use the link beside the level chip until the chip says Whole dial.",
        hint: "Settings is the last tab on the whole dial's tab strip. Open a dial you administer.",
      },
      {
        anchor: HELP_ANCHORS.dialLicensing,
        title: "Choose Licensing",
        body: "Click Licensing in the settings menu.",
        advance: "target-click",
        target: "Licensing entry",
        waitMs: 3000,
        hint: "Only dial admins and organisation instance managers see Licensing.",
      },
      {
        // KEY-813: the explanation belongs beside the figures, not beside the menu entry that opens them.
        anchor: HELP_ANCHORS.dialLicensingSummary,
        title: "What it says",
        body:
          "This says how many places your licence covers and how many are being used. Everyone you add to a dial uses one, including people who can only read, and access to part of a dial costs the same as all of it. Guests are free.",
        waitMs: 3000,
        hint: "Only dial admins and organisation instance managers see Licensing.",
      },
      {
        anchor: HELP_ANCHORS.dialPeople,
        title: "Free a place if you need one",
        body:
          "If every place is taken, People is where you remove somebody who no longer needs access — the figure moves straight away. Watch for archived accounts: archiving stops someone signing in but leaves their dial access, and its place, in use.",
        waitMs: 3000,
        reach: "Click People, the first entry in the Settings menu.",
        hint: "People is the first entry in the Settings menu.",
      },
    ],
  },
  {
    id: "invite-guest",
    title: "Invite a guest",
    summary: "Let someone outside your organisation onto one dial, for a while.",
    keywords: ["guest", "invite", "external", "outside", "visitor", "inspector", "auditor", "temporary", "link", "share"],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.dialSettings,
        title: "Open Settings",
        body: "Guests are set up per dial, on the Settings tab.",
        advance: "target-click",
        target: "Settings tab",
        reach: "Go up to the whole dial — use the link beside the level chip until the chip says Whole dial.",
        hint: "Settings is the last tab on the whole dial's tab strip. Open a dial you administer.",
      },
      {
        anchor: HELP_ANCHORS.dialGuests,
        title: "Choose Guests",
        body: "Guests are kept apart from People: no organisation membership, no licence seat, and access that expires on its own.",
        advance: "target-click",
        target: "Guests entry",
        waitMs: 3000,
        hint: "Only dial admins can invite guests.",
      },
      {
        anchor: HELP_ANCHORS.guestEmail,
        title: "Say who, and how much",
        body: "Enter their email, then read the row as a sentence: which role, which part of the dial, and for how long. Below it you choose, field by field, what they may see — everything starts hidden.",
        waitMs: 4000,
        hint: "If there's no form, guest invites are closed for this dial — the note on the page says why.",
      },
      {
        anchor: HELP_ANCHORS.guestInvite,
        title: "Send the invite",
        body: "Click ‘Send invite’ and we email them the link. It's safe to pass around: all it does is email a sign-in code to the address you typed — so if the email doesn't reach them, you can copy the link shown here and send it yourself.",
        hint: "If there's no form, guest invites are closed for this dial — the note on the page says why.",
      },
    ],
  },
  {
    id: "change-logo",
    title: "Change a dial's logo",
    summary: "Swap the picture in the middle of the dial (dial admins).",
    keywords: ["logo", "image", "picture", "centre", "center", "brand", "hub", "icon"],
    context: "dial",
    steps: [
      {
        anchor: HELP_ANCHORS.dialLogo,
        title: "Click the dial's centre",
        body: "Click the logo in the centre of the dial to open the logo editor. Only dial admins can change it, and only once Key Dials has switched logo changes on for that dial. Each dial is switched on separately, including each dial inside a group.",
        advance: "target-click",
        target: "logo",
        hint: "If you're on the Settings tab, switch to another tab so the dial shows. If the centre isn't clickable, hover it: logo changes aren't switched on for that dial yet, so ask your Key Dials administrator to enable them.",
      },
      {
        anchor: HELP_ANCHORS.logoUpload,
        title: "Upload a new logo",
        body: "Click ‘Upload image…’ and choose a picture. It replaces the centre logo for everyone viewing this dial.",
        waitMs: 4000,
        hint: "Click the logo in the dial's centre to open the editor.",
      },
    ],
  },
  {
    id: "customise-structure",
    title: "Tailor a dial to suit you",
    summary: "Add your own items, hide the ones you don't need, or rename them (dial admins).",
    keywords: ["customise", "customize", "structure", "edit", "items", "hide", "add", "remove", "rename", "tailor"],
    context: "dial",
    // Deliberately untagged: "Structure" is offered on whatever the user can customise (it hangs off
    // `canCustomise`, not the dial's kind) and renaming or hiding sections and action points works the
    // same on both. Only the items it can also add are itemised-dial territory.
    steps: [
      {
        anchor: HELP_ANCHORS.dialSettings,
        title: "Open Settings",
        body: "Everything that changes this dial for everyone on it lives on the Settings tab.",
        advance: "target-click",
        target: "Settings tab",
        reach: "Go up to the whole dial — use the link beside the level chip until the chip says Whole dial.",
        hint: "Settings is the last tab on the whole dial's tab strip. Open a dial you can customise.",
      },
      {
        anchor: HELP_ANCHORS.dialEditStructure,
        title: "Choose Structure",
        body: "Structure is where you tailor the dial — add your own items, hide ones you don't need, or rename them. Your changes save as you make them.",
        advance: "target-click",
        target: "Structure entry",
        hint: "Structure is in the Settings menu. It isn't offered on a group dial.",
        // The settings menu renders with the tab, so this only has to outlast a re-render.
        waitMs: 2000,
      },
    ],
  },
  {
    id: "account-settings",
    title: "Your own settings",
    summary: "Your name, the number texts go to, light or dark, and your PIN.",
    keywords: ["account", "profile", "settings", "me", "my", "name", "phone", "mobile", "number", "text", "sms", "theme", "dark", "light", "pin", "password"],
    // Reachable from the header wherever you are.
    context: "anywhere",
    steps: [
      {
        anchor: HELP_ANCHORS.accountMenu,
        title: "Open your account menu",
        body: "Your picture, top right, opens your own menu — What's New, your settings, and signing out.",
        advance: "target-click",
        target: "picture",
      },
      {
        // KEY-813: choosing Settings was folded into the next step's prose, so the card sat over a page you
        // hadn't opened yet. It is a click of its own now.
        anchor: HELP_ANCHORS.accountSettingsLink,
        title: "Choose Settings",
        body: "Settings opens Your settings — your details, your calendars, your PIN.",
        advance: "target-click",
        target: "Settings item",
        waitMs: 2000,
        hint: "Click your picture, top right, to open the menu again.",
      },
      {
        anchor: HELP_ANCHORS.accountPhone,
        title: "Your details",
        body: "Your mobile number is the one that matters most: without it, a dial can't offer to text you when you subscribe. Your name, light or dark, and your shared-device PIN are on the same page.",
        waitMs: 6000,
        hint: "Choose Settings from your account menu — the page opens on ‘Your details’.",
      },
    ],
  },
  {
    // KEY-813: a seven-day dial is no use until someone's calendar is on it, and that takes two steps in two
    // different places — connect it once, to you, then put it on the dial. This guide walks both. The dial half
    // `requires` a dial with a section of days, so on any other dial it stops after connecting.
    id: "connect-calendar",
    title: "Connect your calendar",
    summary: "Show your Google, Outlook or iCal diary on a seven-day dial.",
    keywords: ["calendar", "calendars", "diary", "google", "outlook", "ical", "ics", "apple", "connect", "events", "appointments", "seven", "days", "week", "sync"],
    context: "anywhere",
    steps: [
      {
        anchor: HELP_ANCHORS.accountMenu,
        title: "Open your account menu",
        body: "A calendar is connected once, to you, and then put on whichever dials should show it. Start with your picture, top right.",
        advance: "target-click",
        target: "picture",
      },
      {
        anchor: HELP_ANCHORS.accountSettingsLink,
        title: "Choose Settings",
        body: "Your calendars live in Your settings.",
        advance: "target-click",
        target: "Settings item",
        waitMs: 2000,
        hint: "Click your picture, top right, to open the menu again.",
      },
      {
        anchor: HELP_ANCHORS.connectedCalendars,
        title: "Connect a calendar",
        body: "Under Connected calendars, connect Google or Outlook, sign in to Apple iCloud with an app-specific password, or paste an iCal link (Airbnb, Booking.com). We only read it — nothing is ever written to your calendar. Press Calendars beside an account to choose which of its calendars to show. Connecting doesn't put it on a dial yet; that's next.",
        waitMs: 6000,
        hint: "Choose Settings from your account menu; Connected calendars is further down Your settings.",
      },
      {
        anchor: HELP_ANCHORS.dialSettings,
        title: "Now open the dial's Settings",
        body: "Back on the dial that should show it, open the Settings tab. (If you connected from a dial, Your settings offers to put it straight on that one.)",
        advance: "target-click",
        target: "Settings tab",
        requires: ["calendars"],
        waitMs: 8000,
        reach: "Go back to your seven-day dial (it's on My Dials) and open its whole-dial view.",
        hint: "Settings is the last tab on the whole dial's tab strip.",
      },
      {
        anchor: HELP_ANCHORS.dialCalendars,
        title: "Choose Calendars",
        body: "Calendars lists whose diaries this dial shows.",
        advance: "target-click",
        target: "Calendars entry",
        requires: ["calendars"],
        waitMs: 3000,
        hint: "Calendars is in the Settings menu, on a dial with a section of days.",
      },
      {
        anchor: HELP_ANCHORS.calendarOnDial,
        title: "Put yours on this dial",
        body: "Pick your calendar and press ‘Show on this dial’. Everyone else on the dial sees your events as Busy unless you tick Share titles.",
        requires: ["calendars"],
        waitMs: 4000,
        hint: "Only this dial's editors and admins can put their calendar on it.",
      },
    ],
  },
];
